<template>
  <f-modal
    :class="{ 'is-visible': isVisible }"
    @close="handleClose">
    <f-modal-header>
      <h3 class="heading-medium">
        <template v-if="editMode">
          {{ $t('inquiryLiabilitiesInfo.editCommitment') }}
        </template>
        <template v-else>
          {{ $t('inquiryLiabilitiesInfo.newCommitment') }}
        </template>
      </h3>
    </f-modal-header>

    <f-modal-body>
      <validation-observer
        ref="inquiry-liability-popup-form"
        tag="form">

        <div
          v-if="isForIncome"
          class="form-group">
          <label for="liability-income">{{ $t('inquiryLiabilitiesInfo.sourceIncome') }}</label>

          <validation-provider
            tag="div"
            rules="required"
            name="liability-income"
            v-slot="{ errors }">
            <div
              :class="{
                'form-select': true,
                'is-invalid': errors.length
              }">
              <v-select
                class="mb-0"
                :options="currentIncomes"
                v-model="selectedIncomeIndex"
                :reduce="(income) => income.index"
                :searchable="true"
                :placeholder="$t('ui.chooseYourSourceOfIncome')"
                label="index">
                <template slot="no-options">
                 {{ $t('ui.noResultsForGivenPhrase') }}
                </template>
                <template slot="option" slot-scope="option">
                  {{ option.index + 1 }}. {{ getIncomTypeName(option.income_type_id) }}
                </template>
                <template slot="selected-option" slot-scope="option">
                  {{ option.index + 1 }}. {{ getIncomTypeName(option.income_type_id)  }}
                </template>
              </v-select>
              <div class="additional-info">
                <f-icon name="info" />
                <span>{{ $t('ui.noResultsForGivenPhrase') }}</span>
              </div>
            </div>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="liability-type">{{ $t('inquiryLiabilitiesInfo.typeOfCommitment') }}</label>

          <validation-provider
            tag="div"
            rules="required"
            name="liability-type"
            v-slot="{ errors }">

            <f-form-radio
              v-for="(liabilityType, index) in allLiabilities"
              :key="index"
              :value="liabilityType.id"
              @change="liabilityTypeChanged"
              v-model="data.liability_id">
              {{ liabilityType.name }}
            </f-form-radio>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isBankSelectionVisible"
          class="form-group">
          <label for="liability-bank">{{ $t('ui.bank') }}</label>

          <validation-provider
            tag="div"
            rules="required"
            name="liability-bank"
            v-slot="{ errors }">
            <div
              :class="{
                'form-select': true,
                'is-invalid': errors.length
              }">
              <v-select
                :options="allBanks"
                v-model="data.bank_id"
                :reduce="(bank) => bank.id"
                :searchable="true"
                :filter="banksSearch"
                :placeholder="$t('ui.chooseBank')"
                label="name">
                <template slot="no-options">
                  {{ $t('ui.noResultsForGivenPhrase') }}
                </template>
              </v-select>
            </div>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="instalment-currency">{{ $t('inquiryLiabilitiesInfo.currencyOfCommitment') }}</label>
          <validation-provider
            tag="div"
            rules="required"
            name="instalment-currency"
            v-slot="{ errors }">
            <div
              :class="{
                'form-select': true,
                'is-invalid': errors.length
              }">
              <v-select
                id="instalment-currency-select"
                :options="allCurrencies"
                v-model="data.instalment_amount_currency_id"
                :reduce="(currency) => currency.id"
                :clearable="false"
                :searchable="false"
                :placeholder="$t('ui.chooseCurrency')"
                label="name">
              </v-select>
            </div>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isInstallmentCurrencyOtherNameVisible"
          class="form-group">
          <label for="instalment-currency-other-name">{{ $t('inquiryLiabilitiesInfo.nameOfCurrencyOfCommitment') }}</label>

          <validation-provider
            tag="div"
            name="instalment-currency-other-name"
            rules="required"
            v-slot="{ errors }">
            <f-input
              v-model="data.instalment_amount_currency_name"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="instalment-amount">
            <template v-if="data.liability_id">
              {{ getLabel() }}
            </template>
            <template v-else>
              {{ $t('inquiryFinancialInfo.installmentAmount') }}
            </template>
          </label>

          <validation-provider
            tag="div"
            rules="required|min_value:1"
            name="instalment-amount"
            v-slot="{ errors }">
            <f-input-wrapper :append="installmentCurrency">
              <f-input
                v-model="data.instalment_amount"
                min="0"
                step="1"
                type="number"
                :state="errors.length ? 'invalid' : null"/>
            </f-input-wrapper>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <template v-if="isBankSelectionVisible">
          <div class="form-group">
            <label for="payoff-currency">{{ $t('inquiryLiabilitiesInfo.currencyAmountRepaid') }}</label>

            <validation-provider
              tag="div"
              rules="required"
              name="payoff-currency"
              v-slot="{ errors }">
              <div
                :class="{
                  'form-select': true,
                  'is-invalid': errors.length
                }">
                <v-select
                  id="payoff-currency-select"
                  :options="allCurrencies"
                  v-model="data.payoff_amount_currency_id"
                  :reduce="(currency) => currency.id"
                  :clearable="false"
                  :searchable="false"
                  :placeholder="$t('ui.chooseCurrency')"
                  label="name">
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div
            v-if="isPayoffCurrencyOtherNameVisible"
            class="form-group">
            <label for="payoff-currency-other-name">{{ $t('inquiryLiabilitiesInfo.nameOfCurrencyAmountRepaid') }}</label>

            <validation-provider
              tag="div"
              name="payoff-currency-other-name"
              rules="required"
              v-slot="{ errors }">
              <f-input
                v-model="data.payoff_amount_currency_name"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="payoff-amount">{{ $t('inquiryLiabilitiesInfo.amountRepaid') }}</label>

            <validation-provider
              tag="div"
              rules="required|min_value:1"
              name="payoff-amount"
              v-slot="{ errors }">
              <f-input-wrapper :append="payoffCurrency">
                <f-input
                  v-model="data.payoff_amount"
                  min="0"
                  step="1"
                  type="number"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </template>

        <div class="form-group">
          <label for="liability-additional-information">{{ $t('inquiryHouseholdInfo.additionalInfo') }}</label>

          <validation-provider
            tag="div"
            name="liability-additional-information"
            v-slot="{ errors }">
            <f-textarea
              v-model="data.additional_information"
              :rows="6"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>
      </validation-observer>

    </f-modal-body>
    <f-modal-footer>
      <div class="flex flex-justify-content-between">
        <f-button
          class="ml-0"
          theme="danger"
          @click.prevent="handleClose">
          {{ $t('ui.cancel') }}
        </f-button>
        <f-button
          class="ml-auto"
          theme="primary"
          :disabled="submitInProgress"
          @click.prevent="validateForm">
          <div
            v-if="submitInProgress"
            class="loading-spinner"
            role="status">
          </div>
          <template v-if="editMode">{{ $t('ui.saveChange') }}</template>
          <template v-else>{{ $t('ui.add') }}</template>
        </f-button>
      </div>
    </f-modal-footer>
  </f-modal>
</template>
<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'inquiry-liability-popup',
  computed: {
    isBankSelectionVisible () {
      if (this.data.liability_id) {
        return !!this.allLiabilities.find(item => item.id === this.data.liability_id).show_bank_select;
      }

      return false
    },
    isPayoffCurrencyOtherNameVisible () {
      if (this.data.payoff_amount_currency_id) {
        let currency = this.allCurrencies.find(item => item.id === this.data.payoff_amount_currency_id).code;
        return currency === 'OTHER';
      }

      return false
    },
    isInstallmentCurrencyOtherNameVisible () {
      if (this.data.instalment_amount_currency_id) {
        let currency = this.allCurrencies.find(item => item.id === this.data.instalment_amount_currency_id).code;
        return currency === 'OTHER';
      }

      return false
    },
    payoffCurrency () {
      if (!this.allCurrencies || !this.allCurrencies.length) {
        return '';
      }
      if (this.data.payoff_amount_currency_id) {
        if (this.isPayoffCurrencyOtherNameVisible) {
          return this.data.payoff_amount_currency_name;
        }

        return this.allCurrencies.find(item => item.id === this.data.payoff_amount_currency_id).code;
      }

      let defaultCurrency = this.allCurrencies.find(item => item.code === 'PLN');

      if (defaultCurrency) {
        return defaultCurrency.code;
      }

      return this.allCurrencies[0].code;
    },
    installmentCurrency () {
      if (!this.allCurrencies || !this.allCurrencies.length) {
        return '';
      }
      if (this.data.instalment_amount_currency_id) {
        if (this.isInstallmentCurrencyOtherNameVisible) {
          return this.data.instalment_amount_currency_name;
        }

        return this.allCurrencies.find(item => item.id === this.data.instalment_amount_currency_id).code;
      }

      let defaultCurrency = this.allCurrencies.find(item => item.code === 'PLN');

      if (defaultCurrency) {
        return defaultCurrency.code;
      }

      return this.allCurrencies[0].code;
    }
  },
  data() {
    return {
      data: {
        id: 0,
        liability_id: null,
        payoff_amount: 0,
        payoff_amount_currency_id: null,
        payoff_amount_currency_name: '',
        instalment_amount: 0,
        instalment_amount_currency_id: null,
        instalment_amount_currency_name: '',
        bank_id: null,
        additional_information: '',
      },
      editMode: false,
      isHidden: true,
      submitInProgress: false,
      allBanks: [],
      allLiabilities: [],
      allIncomeTypes: [],
      allCurrencies: [],
      originalLiability: null,
      isForIncome: false,
      currentIncomes: [],
      isVisible: false,
      selectedIncomeIndex: null,
      originalIncomeIndex: null
    };
  },
  mounted() {
    this.$bus.$on('inquiry-liability-popup-show', this.openPopup);
  },
  methods: {
    handleClose() {
      document.body.classList.remove('no-scroll');
      this.isVisible = false;
    },
    openPopup(liability = null, allBanks = [], allLiabilities = [], allCurrencies = [], allIncomeTypes, currentIncomes = null, incomeIndex = null) {
      Vue.set(this, 'selectedIncomeIndex', incomeIndex);
      Vue.set(this, 'originalIncomeIndex', incomeIndex);
      Vue.set(this, 'isForIncome', false);

      if (currentIncomes && currentIncomes.length) {
        Vue.set(this, 'isForIncome', true);
        this.currentIncomes = JSON.parse(JSON.stringify(currentIncomes));
      }

      this.originalLiability = liability;
      this.editMode = this.originalLiability !== null;

      Vue.set(this, 'allBanks', allBanks);
      Vue.set(this, 'allIncomeTypes', allIncomeTypes);
      Vue.set(this, 'allCurrencies', allCurrencies);

      if (this.isForIncome) {
        Vue.set(this, 'allLiabilities', allLiabilities.filter(item => item.is_business_activity === 1));
      } else {
        Vue.set(this, 'allLiabilities', allLiabilities.filter(item => item.is_business_activity === 0));
      }

      if (this.editMode) {
        this.data.id = liability.id;
        this.data.liability_id = liability.liability_id;
        this.data.payoff_amount = liability.payoff_amount;
        this.data.payoff_amount_currency_id = liability.payoff_amount_currency_id;
        this.data.payoff_amount_currency_name = liability.payoff_amount_currency_name;
        this.data.instalment_amount = liability.instalment_amount;
        this.data.instalment_amount_currency_id = liability.instalment_amount_currency_id;
        this.data.instalment_amount_currency_name = liability.instalment_amount_currency_name;
        this.data.bank_id = liability.bank_id;
        this.data.additional_information = liability.additional_information;
      } else {
        this.data.id = 0;
        this.data.liability_id = null;
        this.data.payoff_amount = 0;
        this.data.payoff_amount_currency_id = null;
        this.data.payoff_amount_currency_name = '';
        this.data.instalment_amount = 0;
        this.data.instalment_amount_currency_id = null;
        this.data.instalment_amount_currency_name = '';
        this.data.bank_id = null;
        this.data.additional_information = '';
      }

      setTimeout(() => {
        this.isVisible = true;
        this.submitInProgress = false;
        document.body.classList.add('no-scroll');
        if (this.$refs['inquiry-liability-popup-form']) {
          this.$refs['inquiry-liability-popup-form'].reset();
        }
      }, 0);
    },
    validateForm () {
      FormUtils.validate(this.$refs['inquiry-liability-popup-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();

      if (this.isForIncome) {
        this.$bus.$emit('inquiry-income-liability-popup-submit', formDataToSend, this.originalLiability, this.editMode, this.selectedIncomeIndex, this.originalIncomeIndex);
      } else {
        if (this.originalLiability) {
          Object.assign(this.originalLiability, formDataToSend);
        }
        this.$bus.$emit('inquiry-liability-popup-submit', formDataToSend, this.editMode);
      }

      this.handleClose();
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        id: this.data.id,
        liability_id: this.data.liability_id,
        payoff_amount: this.data.payoff_amount,
        payoff_amount_currency_id: this.data.payoff_amount_currency_id,
        payoff_amount_currency_name: this.data.payoff_amount_currency_name,
        instalment_amount: this.data.instalment_amount,
        instalment_amount_currency_id: this.data.instalment_amount_currency_id,
        instalment_amount_currency_name: this.data.instalment_amount_currency_name,
        bank_id: this.data.bank_id,
        additional_information: this.data.additional_information
      };

      if (formDataToSend.liability_id === 8) {
        formDataToSend.payoff_amount = 0;
        formDataToSend.payoff_amount_currency_id = 0;
        formDataToSend.payoff_amount_currency_name = '';
        formDataToSend.bank_id = 0;
      }

      return formDataToSend;
    },
    getLabel () {
      let liability = this.allLiabilities.find(item => item.id === this.data.liability_id);
      if (liability && liability.label) {
        return liability.label;
      }

      return '';
    },
    banksSearch(options, search) {
      return options.filter(item => (item.name).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1);
    },
    getIncomTypeName (typeID) {
      if (!typeID) {
        return this.$t('ui.none');
      }

      return this.allIncomeTypes.find(type => type.id === typeID).name;
    },
    liabilityTypeChanged () {
      this.data.payoff_amount = 0;
      this.data.payoff_amount_currency_id = null;
      this.data.payoff_amount_currency_name = '';
      this.data.instalment_amount = 0;
      this.data.instalment_amount_currency_id = null;
      this.data.instalment_amount_currency_name = '';
      this.data.bank_id = null;
    }
  },
  beforeDestroy () {
    this.$bus.$off('inquiry-liability-popup-show', this.openPopup);
  }
}
</script>
